import { socialExport } from '@mi-front/api-clients';
import {
  Avatar,
  Box,
  Chip,
  ListItem,
  ListItemAvatar,
  ListItemProps,
  ListItemText,
} from '@mui/material';
import { defineMessage, useIntl } from 'react-intl';

interface Props extends ListItemProps {
  readonly playlist: socialExport.YoutubePlaylistDto;
}

/**
 * Privacy status labels for YouTube playlists.
 */
const PRIVACY_STATUS_LABELS = {
  private: defineMessage({
    id: 'socialExport.youtube.playlist.privacyStatus.private',
    defaultMessage: 'Privada',
  }),
  public: defineMessage({
    id: 'socialExport.youtube.playlist.privacyStatus.public',
    defaultMessage: 'Público',
  }),
  unlisted: defineMessage({
    id: 'socialExport.youtube.playlist.privacyStatus.unlisted',
    defaultMessage: 'No listado',
  }),
} as const;

/**
 * List item component that displays a YouTube playlist with thumbnail, title, privacy status, and video count.
 * Used in playlist autocomplete for displaying playlist options.
 * Images are lazy-loaded to improve performance when the autocomplete dropdown opens.
 */
export function YoutubePlaylistListItem(props: Props) {
  const { playlist, ...listItemProps } = props;
  const { formatMessage } = useIntl();

  const getPrivacyStatusLabel = (
    status?: socialExport.YoutubePlaylistDtoPrivacyStatusEnumKey,
  ): string => {
    if (!status) {
      return '';
    }

    const label = PRIVACY_STATUS_LABELS[status as keyof typeof PRIVACY_STATUS_LABELS];
    return label ? formatMessage(label) : '';
  };

  return (
    <ListItem {...listItemProps}>
      <ListItemAvatar sx={{ minWidth: 72 }}>
        <Avatar variant="rounded" sx={{ width: 56, height: 32 }}>
          {playlist.thumbnailUrl ? (
            <img
              src={playlist.thumbnailUrl}
              alt={playlist.title}
              loading="lazy"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            playlist.title.charAt(0).toUpperCase()
          )}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={playlist.title}
        secondary={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mt: 0.5,
            }}
          >
            {playlist.privacyStatus && (
              <Chip
                label={getPrivacyStatusLabel(playlist.privacyStatus)}
                size="small"
                variant="outlined"
                sx={{ fontSize: 10, height: 20 }}
              />
            )}
            {playlist.itemCount !== undefined && (
              <Box component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>
                {formatMessage(
                  {
                    id: 'socialExport.youtube.playlist.videoCount',
                    defaultMessage: '{count, plural, one {# video} other {# videos}}',
                  },
                  { count: playlist.itemCount },
                )}
              </Box>
            )}
          </Box>
        }
        primaryTypographyProps={{
          fontSize: 14,
          fontWeight: 500,
        }}
        secondaryTypographyProps={{
          component: 'div',
        }}
      />
    </ListItem>
  );
}
